import React, { useEffect, useRef, useState } from "react";
import _ from "lodash";
import { baseURL } from "../../../constants/endPoints";
import { USER_INFO_KEY } from "../../../constants/common";
import { getLocalStorage } from "../../../helpers/common";

const NOTE_MAX = 600;
const BROKERAGE_MAX = 60;

const computeBoundClass = (length, max) => {
  if (length >= max) return "abr-char-counter is-over";
  if (length >= max - (max === BROKERAGE_MAX ? 8 : 60)) return "abr-char-counter is-near";
  return "abr-char-counter";
};

const trimBase = (url) => (url || "").trim().replace(/\/+$/, "");

const customizationUrl = (inventoryId) =>
  `${trimBase(baseURL)}/api/v2/active-buyer-report/${encodeURIComponent(inventoryId)}/customization`;

const getAccessToken = () => {
  try {
    const info = getLocalStorage(USER_INFO_KEY);
    return _.get(info, "token.accessToken", null);
  } catch (err) {
    return null;
  }
};

const requestCustomization = async (inventoryId, method, body, signal) => {
  const headers = {
    "Content-Type": "application/json",
    "api-key": process.env.REACT_APP_REST_API_KEY || ""
  };
  const accessToken = getAccessToken();
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`;

  const init = { method, headers, signal };
  if (body) init.body = JSON.stringify(body);

  const response = await fetch(customizationUrl(inventoryId), init);
  if (!response.ok) {
    let detail = "";
    try {
      const json = await response.json();
      detail = json?.message || "";
    } catch (err) {
      /* ignore */
    }
    const err = new Error(detail || `Request failed (${response.status})`);
    err.status = response.status;
    throw err;
  }
  const json = await response.json();
  return json && Object.prototype.hasOwnProperty.call(json, "result") ? json.result : json;
};

const EditPanel = ({
  isOpen,
  initialValues,
  recipientName,
  inventoryId,
  onClose,
  onSave,
  onRemoteLoaded,
  onToast
}) => {
  const [note, setNote] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [brokerage, setBrokerage] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const textareaRef = useRef(null);
  const lastFocusedRef = useRef(null);
  const previousIsOpenRef = useRef(false);
  const hydratedRef = useRef(false);

  // One-shot hydrate from API on mount when inventoryId is available
  useEffect(() => {
    if (!inventoryId || hydratedRef.current) return undefined;
    hydratedRef.current = true;
    const controller = new AbortController();
    requestCustomization(inventoryId, "GET", null, controller.signal)
      .then((remote) => {
        if (onRemoteLoaded) onRemoteLoaded(remote);
      })
      .catch((err) => {
        if (err?.name === "AbortError") return;
        // 404 means no saved customization yet - that's fine, fall back to defaults
        if (err?.status === 404) return;
        // eslint-disable-next-line no-console
        console.warn("[EditPanel] failed to hydrate customization from API:", err);
      });
    return () => controller.abort();
  }, [inventoryId, onRemoteLoaded]);

  useEffect(() => {
    if (isOpen) {
      if (!previousIsOpenRef.current) {
        lastFocusedRef.current = typeof document !== "undefined" ? document.activeElement : null;
        setNote(initialValues.note || "");
        setEmail(initialValues.email || "");
        setPhone(initialValues.phone || "");
        setBrokerage(initialValues.brokerage || "");
        setSaveError("");
        previousIsOpenRef.current = true;
        if (typeof document !== "undefined") {
          document.body.style.overflow = "hidden";
        }
        const t = setTimeout(() => {
          if (textareaRef.current) textareaRef.current.focus();
        }, 80);
        return () => clearTimeout(t);
      }
    } else if (previousIsOpenRef.current) {
      previousIsOpenRef.current = false;
      if (typeof document !== "undefined") {
        document.body.style.overflow = "";
      }
      if (lastFocusedRef.current && typeof lastFocusedRef.current.focus === "function") {
        try {
          lastFocusedRef.current.focus();
        } catch (err) {
          /* noop */
        }
      }
    }
    return undefined;
  }, [isOpen, initialValues]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        if (onClose) onClose();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  const handleSave = async () => {
    const payload = {
      note: note.trim(),
      email: email.trim(),
      phone: phone.trim(),
      brokerage: brokerage.trim().slice(0, BROKERAGE_MAX)
    };

    if (!inventoryId) {
      // No backend target - just persist locally via parent
      if (onSave) onSave(payload);
      return;
    }

    setIsSaving(true);
    setSaveError("");
    try {
      const persisted = await requestCustomization(inventoryId, "PUT", payload);
      const merged = {
        note: persisted?.note ?? payload.note,
        email: persisted?.email ?? payload.email,
        phone: persisted?.phone ?? payload.phone,
        brokerage: persisted?.brokerage ?? payload.brokerage
      };
      if (onSave) onSave(merged);
    } catch (err) {
      const msg = err?.message || "Failed to save customizations";
      setSaveError(msg);
      if (onToast) onToast(`Save failed: ${msg}`);
      // Fall back to local-only save so the agent doesn't lose work
      if (onSave) onSave(payload);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <div
        className={`abr-edit-backdrop${isOpen ? " is-open" : ""}`}
        hidden={!isOpen}
        onClick={onClose}
        aria-hidden="true"
      />
      <aside
        className={`abr-edit-panel${isOpen ? " is-open" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="abr-edit-panel-title"
        hidden={!isOpen}
      >
        <div className="abr-edit-panel-header">
          <h3 id="abr-edit-panel-title">Customize this report</h3>
          <button
            className="abr-edit-panel-close"
            type="button"
            aria-label="Close editor"
            onClick={onClose}
          >
            &times;
          </button>
        </div>
        <div className="abr-edit-panel-body">
          <p className="abr-edit-help">
            Add your personal commentary{recipientName ? <> for <strong>{recipientName}</strong></> : null}, and
            confirm the contact info shown to them. Anything you change here saves to your account and renders
            throughout the report.
          </p>

          <div className="abr-edit-section-eyebrow">1 &middot; Personal note</div>
          <div className="abr-edit-field-group">
            <label htmlFor="abr-note-textarea" className="abr-edit-label">Strategic note</label>
            <textarea
              id="abr-note-textarea"
              ref={textareaRef}
              rows={10}
              maxLength={NOTE_MAX}
              placeholder="Share your pricing rationale, market read, and strategy..."
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
            <div className={computeBoundClass(note.length, NOTE_MAX)}>{note.length} / {NOTE_MAX}</div>
            <p className="abr-edit-field-help">
              Leave blank to hide the "Strategic note" section entirely in the seller view.
            </p>
            <details className="abr-edit-tips">
              <summary>Tips for a great note</summary>
              <ul>
                <li>Speak directly to the seller by name</li>
                <li>Reference 1-2 specific buyer matches by what they want</li>
                <li>Explain your pricing rationale in 1-2 sentences</li>
                <li>End with a clear next step (call, meet, etc)</li>
              </ul>
            </details>
          </div>

          <div className="abr-edit-section-divider" />
          <div className="abr-edit-section-eyebrow">2 &middot; Email shown in report</div>
          <div className="abr-edit-field-group">
            <label htmlFor="abr-email-input" className="abr-edit-label">Email address</label>
            <input
              type="email"
              id="abr-email-input"
              placeholder="you@brokerage.com"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <p className="abr-edit-field-help">
              Your MLS-pulled email is prefilled. Swap it for a brokerage email if you'd rather sellers reply there.
            </p>
          </div>

          <div className="abr-edit-section-divider" />
          <div className="abr-edit-section-eyebrow">3 &middot; Phone shown in report</div>
          <div className="abr-edit-field-group">
            <label htmlFor="abr-phone-input" className="abr-edit-label">Phone</label>
            <input
              type="tel"
              id="abr-phone-input"
              placeholder="(480) 555-0101"
              autoComplete="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
            />
            <p className="abr-edit-field-help">Renders in the sticky header, mobile CTA, and contact card.</p>
          </div>

          <div className="abr-edit-section-divider" />
          <div className="abr-edit-section-eyebrow">4 &middot; Brokerage shown in report</div>
          <div className="abr-edit-field-group">
            <label htmlFor="abr-brokerage-input" className="abr-edit-label">Brokerage</label>
            <input
              type="text"
              id="abr-brokerage-input"
              maxLength={BROKERAGE_MAX}
              placeholder="e.g. Russ Lyon Sotheby's International Realty"
              autoComplete="organization"
              value={brokerage}
              onChange={(e) => setBrokerage(e.target.value)}
            />
            <div className={computeBoundClass(brokerage.length, BROKERAGE_MAX)}>{brokerage.length} / {BROKERAGE_MAX}</div>
            <p className="abr-edit-field-help">
              The real estate company where you hang your license (separate from your MLS membership). Renders on the
              cover, sticky header, CTA card, methodology disclosure, and legal strips.
            </p>
          </div>

          {saveError ? (
            <p className="abr-edit-save-error" role="alert">{saveError}</p>
          ) : null}
        </div>
        <div className="abr-edit-panel-footer">
          <button className="abr-btn-cancel" type="button" onClick={onClose} disabled={isSaving}>Cancel</button>
          <button className="abr-btn-save" type="button" onClick={handleSave} disabled={isSaving}>
            {isSaving ? "Saving..." : "Save & preview"}
          </button>
        </div>
      </aside>
    </>
  );
};

export default EditPanel;
